import { Component, DestroyRef, EnvironmentInjector, HostListener, NgModuleRef, effect, inject, OnInit, createNgModule } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { AuthService } from 'src/app/auth/auth.service';
import { SidebarService } from 'src/app/shared/collaboration/sidebar.service';
import { ChatService } from 'src/app/shared/collaboration/chat.service';
import { AgendaService } from 'src/app/shared/collaboration/agenda.service';
import { DialogService } from 'primeng/dynamicdialog';
import { OverlayUsersService } from 'src/app/shared/collaboration/overlay-users.service';
import { HomeUserAvatarDialogComponent } from '../home-user-avatar-dialog/home-user-avatar-dialog.component';
import { HomeNotificationsDialogComponent } from '../home-notifications-dialog/home-notifications-dialog.component';
import { HomeSpecialOrderNotificationsDialogComponent } from '../home-special-order-notifications-dialog/home-special-order-notifications-dialog.component';
import { TasksService } from 'src/app/shared/collaboration/tasks.service';
import { RealtimeStartupCoordinatorService } from 'src/app/shared/collaboration/realtime-startup-coordinator.service';
import { FloatingNotesStateService } from 'src/app/shared/collaboration/floating-notes-state.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { PermissionsService } from 'src/app/auth/permissions.service';
import { TranslateService } from '@ngx-translate/core';
import { SpecialOrderNotificationsService } from 'src/app/shared/collaboration/special-order-notifications.service';
import { NotificationInboxService } from 'src/app/shared/collaboration/notification-inbox.service';
import { PushNotificationService } from 'src/app/shared/collaboration/push-notification.service';
import { UserProfileDialogComponent } from 'src/app/shared/overlays/user-profile-dialog/user-profile-dialog.component';

interface UserToolData {
  avatarUrl?: string | null;
  name?: string;
  email?: string;
  _dpto?: string | null;
  profileTitle?: string | null;
  profileStatus?: string | null;
  profileSupport?: string | null;
  profileAbout?: string | null;
  id?: string | number;
}

@Component({
  standalone: false,
  selector: 'app-home-user-tool',
  templateUrl: './home-user-tool.component.html',
  styleUrls: ['./home-user-tool.component.scss']
})
export class HomeUserToolComponent implements OnInit {
  private shellOptionalModuleRef: NgModuleRef<unknown> | null = null;
  private environmentInjector = inject(EnvironmentInjector);
  public themeService = inject(ThemeService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  private sidebarService = inject(SidebarService);
  private chatService = inject(ChatService);
  private agendaService = inject(AgendaService);
  private tasksService = inject(TasksService);
  private realtimeCoordinator = inject(RealtimeStartupCoordinatorService);
  private dialogService = inject(DialogService);
  private overlayUsersService = inject(OverlayUsersService);
  private floatingNotesService = inject(FloatingNotesStateService);
  private permissionsService = inject(PermissionsService);
  private translate = inject(TranslateService);
  private specialOrderNotifications = inject(SpecialOrderNotificationsService);
  private notificationInbox = inject(NotificationInboxService);
  private pushNotifications = inject(PushNotificationService);
  private destroyRef = inject(DestroyRef);
  readonly unreadNotifications = this.notificationInbox.unreadCount;
  readonly unreadSpecialOrderNotifications = this.specialOrderNotifications.unreadCount;

  logedUser: UserToolData | null = this.authService.getUserFromLocalStorage();
  unreadMessages = 0;
  pendingAgenda = 0;
  pendingTasks = 0;
  qrLoading = false;
  qrLoadError = false;

  constructor() {
    effect(() => {
      this.notificationInbox.unreadCount();
      this.specialOrderNotifications.unreadCount();
      this.syncAppBadge();
    });
  }

  get pendingTasksBadge(): number | string {
    return this.pendingTasks > 99 ? '99+' : this.pendingTasks;
  }

  ngOnInit(): void {
    this.updateCounts();

    this.chatService.contacts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.unreadMessages = this.chatService.getTotalUnreadMessages();
        this.syncAppBadge();
      }
    });

    this.agendaService.events$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.pendingAgenda = this.agendaService.getTotalUpcomingEvents();
        this.syncAppBadge();
      }
    });

    this.tasksService.summary$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.pendingTasks = this.tasksService.getTotalPendingTasks();
        this.syncAppBadge();
      }
    });

    this.specialOrderNotifications.refreshUnreadCount().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.pushNotifications.notificationReceived$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.notificationInbox.refreshUnreadCount().pipe(takeUntilDestroyed(this.destroyRef)).subscribe());
    this.authService.user
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        if (user?.id) {
          this.notificationInbox.refreshUnreadCount().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        } else {
          this.notificationInbox.reset();
          this.syncAppBadge();
        }
      });
  }

  updateCounts(): void {
    this.unreadMessages = this.chatService.getTotalUnreadMessages();
    this.pendingAgenda = this.agendaService.getTotalUpcomingEvents();
    this.pendingTasks = this.tasksService.getTotalPendingTasks();
    this.syncAppBadge();
  }

  @HostListener('window:focus')
  refreshNotificationCount(): void {
    this.notificationInbox.refreshUnreadCount().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private syncAppBadge(): void {
    this.realtimeCoordinator.updateAppBadgeCount(
      Number(this.unreadMessages || 0)
      + Number(this.pendingAgenda || 0)
      + Number(this.pendingTasks || 0)
      + Number(this.unreadNotifications() || 0)
      + Number(this.unreadSpecialOrderNotifications() || 0)
    );
  }

  onOpenChat(): void {
    void this.realtimeCoordinator.ensureChatReady();
    this.sidebarService.closeSidebar();
    this.router.navigate(['/mensajes']);
  }

  onOpenAgenda(): void {
    void this.realtimeCoordinator.ensureAgendaReady();
    this.sidebarService.requestOverlayLoad();
    this.sidebarService.openAgendaSidebar();
  }

  onOpenTasks(): void {
    this.sidebarService.requestOverlayLoad();
    this.sidebarService.openTasksSidebar();
  }

  onOpenNotes(): void {
    this.floatingNotesService.requestWidgetLoad();
    if (this.floatingNotesService.visible) {
      this.floatingNotesService.close();
    } else {
      this.floatingNotesService.reopen();
    }
  }

  onOpenNotificationInbox(): void {
    this.dialogService.open(HomeNotificationsDialogComponent, {
      header: 'Notificaciones',
      width: 'min(58rem, calc(100vw - 2rem))',
      height: 'min(48rem, calc(100vh - 2rem))',
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      styleClass: 'home-notifications-dialog'
    });
  }

  onOpenSpecialOrderNotifications(): void {
    this.dialogService.open(HomeSpecialOrderNotificationsDialogComponent, {
      header: 'Avisos de Compras',
      width: 'min(58rem, calc(100vw - 2rem))',
      height: 'min(44rem, calc(100vh - 2rem))',
      closable: false,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      styleClass: 'home-special-order-notifications-dialog'
    });
  }

  get canScanQr(): boolean {
    return this.permissionsService.canAny([
      'purchase-v2:trace:view',
      'inventory:items:edit',
    ]);
  }

async onOpenQrScanner(): Promise<void> {
    if (this.qrLoading) {
      return;
    }

    this.qrLoading = true;
    this.qrLoadError = false;
    try {
      const optionalShell = await import('../../shell-optional.module');
      this.shellOptionalModuleRef ??= createNgModule(
        optionalShell.ShellOptionalModule,
        this.environmentInjector,
      );

      const ref = this.dialogService.open(optionalShell.HomeQrScannerDialogComponent, {
        header: this.translate.instant('USER.TOOL.QR_SCAN'),
        width: '34rem',
        modal: true,
        closable: true,
        closeOnEscape: true,
        dismissableMask: true,
        styleClass: 'home-qr-scanner-dialog',
        data: {
          department: this.logedUser?._dpto || null,
          canViewTrace: this.permissionsService.can('purchase-v2:trace:view'),
          canViewFinancial: this.permissionsService.canAny([
            'purchase-v2:invoice:match',
            'inventory:items:edit',
          ]),
          mode: 'operate',
          canMoveStock: false,
          canMarkDamaged: this.permissionsService.can('inventory:items:edit'),
          canMarkNoVender: this.permissionsService.can('inventory:items:edit'),
          canManageConditions: this.permissionsService.can('inventory:items:edit'),
        },
      });

      ref?.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result: { openTrace?: string } | undefined) => {
          if (result?.openTrace) {
            this.router.navigate(['/compras-v2/trazabilidad', result.openTrace]);
          }
        },
      });
    } catch (error) {
      this.qrLoadError = true;
      console.error('Error loading QR scanner', error);
    } finally {
      this.qrLoading = false;
    }
  }

  onLogout(): void {
    this.confirmationService.confirm({
      message: this.translate.instant('USER.TOOL.CONFIRM_LOGOUT'),
      header: this.translate.instant('USER.TOOL.CONFIRM_LOGOUT'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('COMMON.YES'),
      rejectLabel: this.translate.instant('COMMON.NO'),
      accept: () => {
        void this.finishLogout();
      }
    });
  }

  private async finishLogout(): Promise<void> {
    await this.authService.logout();
    await this.router.navigate(["/login"]);
  }

  onOpenAvatarDialog(): void {
    const ref = this.dialogService.open(HomeUserAvatarDialogComponent, {
      header: this.translate.instant('USER.TOOL.CHANGE_AVATAR'),
      width: '30rem',
      closable: true,
      modal: true,
      closeOnEscape: true,
      data: {
        user: this.logedUser
      },
      dismissableMask: true,
      styleClass: 'home-user-avatar-dialog'
    });

    ref?.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updatedUser: UserToolData) => {
        if (!updatedUser) {
          return;
        }

        this.logedUser = {
          ...this.logedUser,
          ...updatedUser
        };

        this.overlayUsersService.updateUserAvatar(
          Number(this.logedUser?.id || 0),
          this.logedUser?.avatarUrl || ''
        );
      }
    });
  }

  onOpenProfile(): void {
    const user = this.authService.user.value || this.logedUser;
    if (!user) {
      return;
    }

    const ref = this.dialogService.open(UserProfileDialogComponent, {
      header: this.translate.instant('USER.PROFILE.TITLE'),
      width: 'min(34rem, calc(100vw - 2rem))',
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      styleClass: 'user-profile-dialog',
      data: { user },
    });

    ref?.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updatedUser: UserToolData | undefined) => {
        if (!updatedUser) {
          return;
        }
        const current = this.authService.user.value;
        this.logedUser = current || { ...this.logedUser, ...updatedUser };
        const profile = {
          profileTitle: updatedUser.profileTitle || null,
          profileStatus: updatedUser.profileStatus || null,
          profileSupport: updatedUser.profileSupport || null,
          profileAbout: updatedUser.profileAbout || null,
        };
        const userId = Number(updatedUser.id || this.logedUser?.id || 0);
        this.overlayUsersService.updateUserProfile(userId, profile);
        this.chatService.updateUserProfile(userId, profile);
      },
    });
  }
}
